import type { ReactNode } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { HeroUIProvider } from '@heroui/react';

import { Analytics } from '@dub/analytics/react';

import { AuthProvider } from '@/features/auth/ui/auth-provider';

import { ENV } from '@/shared/config/env';
import { StripeProvider } from '@/shared/providers/stripe-provider';
import { WebSocketProvider } from '@/shared/providers/websocket-provider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Make queryClient available globally for logout
if (typeof window !== 'undefined') {
  (window as unknown as { __REACT_QUERY_CLIENT__: QueryClient }).__REACT_QUERY_CLIENT__ =
    queryClient;
}

interface ProvidersProps {
  children: ReactNode;
}

export const Providers = ({ children }: ProvidersProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <Analytics publishableKey={ENV.DUB_PUBLISHABLE_KEY} />
        <StripeProvider>
          <HeroUIProvider>
            <AuthProvider>
              <WebSocketProvider>
                <div className="dark bg-background text-foreground min-h-screen">{children}</div>
                <Toaster
                  position="top-center"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#18181b',
                      color: '#fff',
                      border: '1px solid #27272a',
                    },
                  }}
                />
              </WebSocketProvider>
            </AuthProvider>
          </HeroUIProvider>
        </StripeProvider>
      </HelmetProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};
